import { toOxford } from '@core/utils/oxford'
import Icon from '@admin/components/icon'
import T from '@admin/components/t'
import PropTypes from 'prop-types'
import React from 'react'
import { useDrag, useDrop } from 'react-dnd'

const ItemTypes = {
  CRITERIA_ITEM: 'criteria_item'
}

const comparisons = {
  $lk: 't(contains)',
  $nlk: 't(does not contain)',
  $kn: 't(is known)',
  $nkn: 't(is not known)',
  $eq: 't(is)',
  $neq: 't(is not)',
  $gt: 't(is greater than)',
  $jgt: 't(is greater than)',
  $lt: 't(is less than)',
  $jlt: 't(is less than)',
  $gte: 't(is greater than or equal to)',
  $lte: 't(is less than or equal to)',
  $in: 't(is)',
  $nin: 't(is not)',
  $ck: 't(is checked)',
  $nck: 't(is not checked)',
  $ply: 't(is inside polygon)',
  $nply: 't(is not inside polygon)'
}

const DraggableItem = ({ children, criteria, onMove, disabled = false }) => {
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.CRITERIA_ITEM,
    item: { 
      code: criteria.code,
      parentCode: criteria.parent
    },
    canDrag: !disabled,
    collect: (monitor) => ({
      isDragging: monitor.isDragging()
    })
  })

  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ItemTypes.CRITERIA_ITEM,
    drop: (draggedItem, monitor) => {
      if (!monitor.didDrop()) {
        // Only handle drops into AND/OR containers (group moves)
        if (_.includes(['$and','$or'], criteria.operator)) {
          onMove(draggedItem.code, criteria.code)
        }
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop()
    })
  })

  return (
    <div 
      ref={(node) => drag(drop(node))}
      style={{ opacity: isDragging ? 0.5 : 1 }}
      className={`draggable-item ${isDragging ? 'dragging' : ''} ${isOver && canDrop ? 'drop-target' : ''}`}
    >
      {children}
    </div>
  )
}

DraggableItem.propTypes = {
  children: PropTypes.node.isRequired,
  criteria: PropTypes.object.isRequired,
  onMove: PropTypes.func.isRequired,
  disabled: PropTypes.bool
}

DraggableItem.defaultProps = {
  disabled: false
}

// Droppable operator label component
const DropLabel = ({ children, parentCode, position, onMove }) => {
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: ItemTypes.CRITERIA_ITEM,
    drop: (draggedItem, monitor) => {
      if (!monitor.didDrop()) {
        // Move item to this position in the parent
        onMove(draggedItem.code, parentCode, position)
      }
    },
    collect: (monitor) => ({
      isOver: monitor.isOver({ shallow: true }),
      canDrop: monitor.canDrop()
    })
  })

  return (
    <div 
      ref={drop}
      className={`maha-criteria-operator-label ${isOver && canDrop ? 'drop-target' : ''}`}
    >
      {children}
    </div>
  )
}

DropLabel.propTypes = {
  children: PropTypes.node.isRequired,
  parentCode: PropTypes.string.isRequired,
  position: PropTypes.number.isRequired,
  onMove: PropTypes.func.isRequired
}

class Item extends React.Component {

  static contextTypes = {
    locale: PropTypes.object
  }

  static propTypes = {
    criteria: PropTypes.object,
    fields: PropTypes.array,
    onAdd: PropTypes.func,
    onMove: PropTypes.func,
    onRemove: PropTypes.func
  }

  render() {
    const { criteria, onMove } = this.props
    
    if(_.includes(['$and','$or'], criteria.operator)) {
      const operatorLabel = criteria.operator.replace('$', '').toUpperCase()
      
      return (
        <DraggableItem 
          criteria={criteria} 
          onMove={onMove} 
          disabled={criteria.index === 0} // Don't allow dragging the root item
        >
          <div className={`maha-criteria-item ${criteria.operator.replace('$', '')}`}>
            <div className="maha-criteria-item-box">
              { criteria.index > 0 &&
                <div className="maha-criteria-item-remove" onClick={ this._handleRemove.bind(this, criteria) }>
                  <Icon svg="x" />
                </div>
              }
              { criteria.items.map((item, index) => (
                <div key={`item_${index}`}>
                  <Item { ...this._getItem(item) } />
                  { index < criteria.items.length - 1 && (
                    <DropLabel 
                      parentCode={criteria.code} 
                      position={index + 1} 
                      onMove={onMove}
                    >
                      {operatorLabel}
                    </DropLabel>
                  )}
                </div>
              )) }
              { criteria.items.length > 0 && (
                <DropLabel 
                  parentCode={criteria.code} 
                  position={criteria.items.length} 
                  onMove={onMove}
                >
                  {operatorLabel}
                </DropLabel>
              )}
              <div className="ui mini compact button" onClick={ this._handleNew.bind(this, criteria) }>
                <Icon svg="plus" /> <T text="t(Add Criteria)" />
              </div>
            </div>
          </div>
        </DraggableItem>
      )
    }
    
    return (
      <DraggableItem criteria={criteria} onMove={onMove}>
        <div className="maha-criteria-item">
          <div className="maha-criteria-item-box">
            <span dangerouslySetInnerHTML={{ __html: this._getDescription(criteria) }} />
            <div className="maha-criteria-item-remove" onClick={ this._handleRemove.bind(this, criteria) }>
              <Icon svg="x" />
            </div>
          </div>
        </div>
      </DraggableItem>
    )
  }

  _getDescription(item) {
    const fields = this._getFields()
    const field = _.find(fields, { key: item.field })
    const phrase = []
    if(field.subject !== false) phrase.push(`<strong>${field.name}</strong>`)
    const operator = this._getOperator(field, item.operator)
    const value = this._getValue(item, item.operator)
    if(operator) phrase.push(operator)
    if(value) phrase.push(value)
    return _.capitalize(this.context.locale.t(phrase.join(' ')))
  }

  _getFields() {
    const { fields } = this.props
    return fields.reduce((fields, segment) => [
      ...fields,
      ...segment.fields
    ], [])
  }

  _getItem(criteria) {
    const { fields, onAdd, onMove, onRemove} = this.props
    return {
      criteria,
      fields,
      onAdd,
      onMove,
      onRemove
    }
  }

  _getOperator(field, operator) {
    if(['$in','$jin','$nin','$njin','$nl','$nnl'].includes(operator)) return ''
    if(field.comparisons) {
      const comparison = _.find(field.comparisons, { value: operator })
      if(comparison) return comparison.text
    }
    return comparisons[operator]
  }

  _getValue(item, operator) {
    if(['$nl','$nnl','$jnl','$njnl','$atta'].includes(operator)) return ''
    if(item?.data?.text) return item.data.text
    if(item?.data?.length > 0) return this._getList(item.data, item.operator)
    return this._getList(item.value, item.operator)
  }

  _getList(items, operator) {
    const list = _.castArray(items).map(item => {
      return `<strong>${item?.text || item?.value || item}</strong>`
    })
    const [word, conjunction] = ['$nin','$njin'].includes(operator) ? ['neither','nor'] : ['either','or']
    return list.length > 1 ? `is ${word} ${toOxford(list, conjunction)}` : list[0]
  }

  _handleNew(criteria) {
    this.props.onAdd(criteria.code)
  }

  _handleRemove(criteria, e) {
    e.stopPropagation()
    this.props.onRemove(criteria.index)
  }
}

export default Item
